import { visibleText } from './rules'

/** A camera or microphone as a picker lists it. */
export interface DeviceChoice {
  deviceId: string
  label: string
}

/** The browser's own entries that stand for another device ("Default - MacBook microphone"; Windows' communications device). */
export const BROWSER_DEFAULTS = new Set(['default', 'communications'])

const NOUN = { videoinput: 'camera', audioinput: 'microphone' } as const

/**
 * One kind of device as the camera check's pickers list it: each device once (the browser's default and communications
 * entries go when they repeat a listed device of the same group), by the name the browser gives it. Without one (before
 * permission, or in some browsers) never a blank option: "Default microphone" for the browser's default, "Communications
 * microphone" for Windows', and the others numbered in order, "Microphone 1", "Microphone 2" ("Camera 1" for cameras).
 * Entries without an id (the browser hiding its devices) are left out.
 */
export function deviceChoices(devices: readonly MediaDeviceInfo[], kind: 'videoinput' | 'audioinput'): DeviceChoice[] {
  const usable = devices.filter((device) => device.kind === kind && device.deviceId)
  const repeats = (device: MediaDeviceInfo) =>
    BROWSER_DEFAULTS.has(device.deviceId) && usable.some((other) => !BROWSER_DEFAULTS.has(other.deviceId) && other.groupId === device.groupId)
  const noun = NOUN[kind]
  let numbered = 0
  return usable
    .filter((device) => !repeats(device))
    .map((device) => {
      const named = visibleText(device.label ?? '')
      if (named) return { deviceId: device.deviceId, label: named }
      if (device.deviceId === 'default') return { deviceId: device.deviceId, label: `Default ${noun}` }
      if (device.deviceId === 'communications') return { deviceId: device.deviceId, label: `Communications ${noun}` }
      numbered += 1
      return { deviceId: device.deviceId, label: `${noun[0].toUpperCase()}${noun.slice(1)} ${numbered}` }
    })
}
